import type { FacetEditorialMap } from "./types";

/** Contenu éditorial par forme de monture (ids alignés sur FACET_REGISTRY.shape). */
export const SHAPE_EDITORIAL: FacetEditorialMap = {
  round: {
    illustration: "round",
    faceShapes: ["square", "heart"],
    related: [
      { key: "material", valueId: "metal" },
      { key: "color", valueId: "gold" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "Les lunettes rondes sont-elles faites pour vous ?",
      suits:
        "La monture ronde adoucit les traits marqués. Elle équilibre particulièrement bien les visages carrés ou rectangulaires, dont elle casse les angles de la mâchoire et du front. Sur un visage rond, elle accentue au contraire la rondeur : mieux vaut alors une forme rectangulaire ou carrée.",
      keyPoints: [
        { title: "Style", body: "Un classique intemporel, entre rétro et minimalisme, qui fonctionne aussi bien au bureau qu'en week-end." },
        { title: "Confort", body: "Verres compacts, monture légère : le poids reste réparti sur le nez et non sur les tempes." },
        { title: "Verres", body: "Le champ de vision réduit convient très bien au simple foyer et à l'anti-lumière bleue ; pour un progressif, préférez un cercle assez haut." },
      ],
      advice:
        "Sur une monture ronde en métal, resserrez les plaquettes une à deux fois par an chez votre opticien : c'est ce qui empêche la paire de glisser. Le nettoyage se fait à l'eau tiède savonneuse, jamais à l'alcool.",
      faq: [
        { q: "Les lunettes rondes vont-elles à un visage rond ?", a: "Rarement de façon flatteuse : elles renforcent la rondeur. Sur un visage rond, les formes rectangulaires, carrées ou browline structurent davantage le regard." },
        { q: "Les lunettes rondes conviennent-elles aux verres progressifs ?", a: "Oui, à condition de choisir un cercle suffisamment haut (28 mm minimum) pour loger les trois zones de vision. Notre opticien vérifie cette hauteur gratuitement en magasin." },
        { q: "Quelle matière choisir pour une monture ronde ?", a: "Le métal fin donne un rendu rétro et discret, l'acétate un rendu plus affirmé et coloré. Les deux sont disponibles dans notre catalogue." },
      ],
    },
    en: {
      suitsTitle: "Are round glasses right for you?",
      suits:
        "Round frames soften strong features. They balance square and rectangular faces particularly well by breaking the angles of the jaw and forehead. On a round face they emphasise roundness instead, so a rectangle or square shape usually works better.",
      keyPoints: [
        { title: "Style", body: "A timeless classic, between retro and minimal, that works at the office and at the weekend." },
        { title: "Comfort", body: "Compact lenses and a light frame keep the weight on the nose rather than the temples." },
        { title: "Lenses", body: "The compact field suits single-vision and blue-light lenses very well; for progressives, pick a taller lens height." },
      ],
      advice:
        "On a round metal frame, have the nose pads adjusted once or twice a year: that is what stops the pair from sliding. Clean with lukewarm soapy water, never alcohol.",
      faq: [
        { q: "Do round glasses suit a round face?", a: "Rarely in a flattering way, as they reinforce roundness. On a round face, rectangle, square or browline shapes structure the look better." },
        { q: "Can round glasses take progressive lenses?", a: "Yes, provided the lens height is tall enough (28 mm minimum) to fit the three vision zones. Our optician checks this for free in store." },
        { q: "Which material works best for a round frame?", a: "Thin metal gives a discreet retro finish, acetate a bolder and more colourful one. Both are available in our catalogue." },
      ],
    },
  },

  rectangle: {
    illustration: "rectangle",
    faceShapes: ["round", "oval"],
    related: [
      { key: "material", valueId: "plastic" },
      { key: "gender", valueId: "men" },
      { key: "color", valueId: "black" },
    ],
    fr: {
      suitsTitle: "Les lunettes rectangulaires sont-elles faites pour vous ?",
      suits:
        "C'est la forme la plus polyvalente du catalogue. Ses lignes horizontales allongent visuellement un visage rond et structurent un visage ovale. Elle reste discrète en réunion et suffisamment large pour les verres progressifs.",
      keyPoints: [
        { title: "Style", body: "Sobre et professionnel : la valeur sûre quand on porte ses lunettes toute la journée." },
        { title: "Confort", body: "Cercles larges, appui stable sur le nez, branches droites : peu de points de pression." },
        { title: "Verres", body: "Forme idéale pour le progressif et le mi-distance écran, grâce à la hauteur de verre disponible." },
      ],
      advice:
        "Une monture rectangulaire doit s'arrêter au bord du visage, sans dépasser les tempes. Nos opticiens mesurent l'écart pupillaire et la largeur du visage gratuitement lors du test de vue.",
      faq: [
        { q: "Les lunettes rectangulaires conviennent-elles au travail sur écran ?", a: "Oui, c'est même la forme la plus recommandée : la hauteur de verre permet un traitement anti-lumière bleue et une zone intermédiaire confortable." },
        { q: "Quelle différence entre rectangulaire et carrée ?", a: "La rectangulaire est plus large que haute, avec des angles adoucis ; la carrée a des proportions plus équilibrées et un rendu plus affirmé." },
        { q: "Les lunettes rectangulaires vont-elles aux femmes comme aux hommes ?", a: "Oui, c'est la forme la plus unisexe. La différence se joue surtout sur l'épaisseur de la monture et la couleur." },
      ],
    },
    en: {
      suitsTitle: "Are rectangle glasses right for you?",
      suits:
        "This is the most versatile shape in the catalogue. Its horizontal lines visually lengthen a round face and structure an oval one. It stays discreet in meetings and is wide enough for progressive lenses.",
      keyPoints: [
        { title: "Style", body: "Understated and professional: the safe pick when you wear your glasses all day." },
        { title: "Comfort", body: "Wide lenses, stable nose rest and straight temples mean few pressure points." },
        { title: "Lenses", body: "The ideal shape for progressive and screen lenses thanks to the available lens height." },
      ],
      advice:
        "A rectangle frame should stop at the edge of your face without overhanging the temples. Our opticians measure pupillary distance and face width for free during the eye test.",
      faq: [
        { q: "Are rectangle glasses good for screen work?", a: "Yes, it is the most recommended shape: the lens height allows a blue-light coating and a comfortable intermediate zone." },
        { q: "What is the difference between rectangle and square?", a: "Rectangle frames are wider than they are tall with softened corners; square frames have more balanced proportions and a bolder look." },
        { q: "Do rectangle glasses suit both men and women?", a: "Yes, it is the most unisex shape. The difference comes mainly from frame thickness and colour." },
      ],
    },
  },

  square: {
    illustration: "square",
    faceShapes: ["round", "oval"],
    related: [
      { key: "material", valueId: "plastic" },
      { key: "color", valueId: "black" },
      { key: "gender", valueId: "men" },
    ],
    fr: {
      suitsTitle: "Les lunettes carrées sont-elles faites pour vous ?",
      suits:
        "La monture carrée apporte du caractère. Elle structure les visages ronds et ovales en marquant la ligne des sourcils. Sur un visage déjà anguleux, elle peut durcir les traits : préférez alors une forme ronde ou ovale.",
      keyPoints: [
        { title: "Style", body: "Affirmé et contemporain, très porté en acétate épais." },
        { title: "Confort", body: "Bonne stabilité grâce à des branches larges, à condition d'ajuster la longueur derrière l'oreille." },
        { title: "Verres", body: "Compatible avec tous les types de verres, y compris progressifs et photochromiques." },
      ],
      advice:
        "Sur une monture carrée en acétate, évitez de la poser verres vers le bas : les angles s'usent vite. Un étui rigide suffit à doubler la durée de vie de la paire.",
      faq: [
        { q: "Les lunettes carrées grossissent-elles le visage ?", a: "Non, mais elles élargissent visuellement le haut du visage. C'est justement ce qui équilibre un menton marqué ou un visage rond." },
        { q: "Peut-on porter des lunettes carrées au quotidien ?", a: "Oui. Choisissez une épaisseur moyenne et une couleur neutre si vous les portez huit heures par jour au bureau." },
      ],
    },
    en: {
      suitsTitle: "Are square glasses right for you?",
      suits:
        "Square frames add character. They structure round and oval faces by defining the brow line. On an already angular face they can harden the features, so a round or oval shape usually works better.",
      keyPoints: [
        { title: "Style", body: "Bold and contemporary, especially in thick acetate." },
        { title: "Comfort", body: "Good stability thanks to wide temples, provided the length behind the ear is adjusted." },
        { title: "Lenses", body: "Compatible with every lens type, including progressive and photochromic." },
      ],
      advice:
        "With a square acetate frame, avoid resting it lens-down: the corners wear quickly. A hard case alone doubles the life of the pair.",
      faq: [
        { q: "Do square glasses make the face look bigger?", a: "No, but they visually widen the upper face, which is exactly what balances a strong chin or a round face." },
        { q: "Can square glasses be worn every day?", a: "Yes. Choose a medium thickness and a neutral colour if you wear them eight hours a day at the office." },
      ],
    },
  },

  aviator: {
    illustration: "aviator",
    faceShapes: ["square", "heart"],
    related: [
      { key: "material", valueId: "metal" },
      { key: "color", valueId: "gold" },
      { key: "gender", valueId: "men" },
    ],
    fr: {
      suitsTitle: "Les lunettes aviateur sont-elles faites pour vous ?",
      suits:
        "L'aviateur, avec sa goutte inversée et son double pont, adoucit les visages carrés et rééquilibre les visages en cœur en élargissant le bas. C'est aussi la forme la plus demandée en version solaire.",
      keyPoints: [
        { title: "Style", body: "Iconique et intemporel, décliné en métal fin ou en version optique plus large." },
        { title: "Confort", body: "Plaquettes réglables : l'aviateur est l'une des rares formes qui s'ajuste précisément à toutes les racines de nez." },
        { title: "Verres", body: "Excellent en solaire et en photochromique ; en progressif, vérifiez la hauteur du verre avec l'opticien." },
      ],
      advice:
        "Le double pont peut se détendre avec le temps. Un resserrage gratuit en boutique Lapaire suffit à retrouver le maintien d'origine.",
      faq: [
        { q: "L'aviateur existe-t-il en verres correcteurs ?", a: "Oui. Toutes nos montures aviateur peuvent recevoir des verres correcteurs, simples foyers comme progressifs." },
        { q: "L'aviateur convient-il aux femmes ?", a: "Oui, notamment en métal doré fin, qui reste l'un des modèles les plus vendus toutes catégories confondues." },
      ],
    },
    en: {
      suitsTitle: "Are aviator glasses right for you?",
      suits:
        "With its teardrop lenses and double bridge, the aviator softens square faces and rebalances heart-shaped ones by widening the lower half. It is also the most requested shape in sunglasses.",
      keyPoints: [
        { title: "Style", body: "Iconic and timeless, available in thin metal or in a wider optical version." },
        { title: "Comfort", body: "Adjustable nose pads make the aviator one of the few shapes that fits every nose bridge precisely." },
        { title: "Lenses", body: "Excellent in sun and photochromic lenses; for progressives, check lens height with the optician." },
      ],
      advice:
        "The double bridge can loosen over time. A free adjustment in any Lapaire store restores the original fit.",
      faq: [
        { q: "Are aviators available with prescription lenses?", a: "Yes. All our aviator frames can take prescription lenses, single vision or progressive." },
        { q: "Do aviators suit women?", a: "Yes, especially in thin gold metal, which remains one of our best sellers across all categories." },
      ],
    },
  },

  cateye: {
    illustration: "cateye",
    faceShapes: ["round", "square"],
    related: [
      { key: "gender", valueId: "women" },
      { key: "material", valueId: "plastic" },
      { key: "color", valueId: "brown" },
    ],
    fr: {
      suitsTitle: "Les lunettes œil-de-chat sont-elles faites pour vous ?",
      suits:
        "La forme œil-de-chat relève le regard : ses angles remontants ouvrent l'œil et affinent les pommettes. Elle flatte les visages ronds et carrés et reste l'une des formes les plus demandées chez les femmes.",
      keyPoints: [
        { title: "Style", body: "Rétro assumé ou version modernisée plus douce, selon l'épaisseur de la monture." },
        { title: "Confort", body: "Monture légère en acétate, avec un appui nasal généralement large et stable." },
        { title: "Verres", body: "Parfaite en simple foyer et en solaire ; en progressif, vérifiez que la partie basse du verre est assez haute." },
      ],
      advice:
        "Sur une œil-de-chat, la pointe extérieure doit rester au-dessus de la ligne des yeux. Un décalage vers le bas ferme le regard : l'ajustage en boutique est gratuit.",
      faq: [
        { q: "L'œil-de-chat convient-il à un visage rond ?", a: "Oui, c'est même l'une des meilleures options : les angles remontants cassent la rondeur et allongent le visage." },
        { q: "Peut-on porter une œil-de-chat au bureau ?", a: "Oui, en choisissant une monture fine et une couleur neutre (écaille, noir, marron) plutôt qu'un modèle très marqué." },
      ],
    },
    en: {
      suitsTitle: "Are cat-eye glasses right for you?",
      suits:
        "The cat-eye lifts the gaze: its upswept corners open the eye and slim the cheekbones. It flatters round and square faces and remains one of the most requested shapes among women.",
      keyPoints: [
        { title: "Style", body: "Full retro or a softer modern take, depending on frame thickness." },
        { title: "Comfort", body: "Light acetate frame with a wide, stable nose rest." },
        { title: "Lenses", body: "Perfect in single vision and sun lenses; for progressives, check that the lower lens area is tall enough." },
      ],
      advice:
        "On a cat-eye, the outer tip should stay above the eye line. If it drops, the gaze looks closed — in-store adjustment is free.",
      faq: [
        { q: "Does the cat-eye suit a round face?", a: "Yes, it is one of the best options: the upswept corners break the roundness and lengthen the face." },
        { q: "Can a cat-eye be worn at the office?", a: "Yes, by choosing a thin frame and a neutral colour (tortoise, black, brown) rather than a very statement model." },
      ],
    },
  },

  oval: {
    illustration: "oval",
    faceShapes: ["square", "heart"],
    related: [
      { key: "material", valueId: "metal" },
      { key: "gender", valueId: "unisex" },
      { key: "size", valueId: "medium" },
    ],
    fr: {
      suitsTitle: "Les lunettes ovales sont-elles faites pour vous ?",
      suits:
        "L'ovale est la forme la plus neutre : ni ronde ni anguleuse, elle passe partout et adoucit les visages carrés ou triangulaires sans attirer l'attention sur la monture.",
      keyPoints: [
        { title: "Style", body: "Discret et sobre, idéal pour une première paire ou un usage professionnel." },
        { title: "Confort", body: "Verres compacts, monture légère : c'est souvent le meilleur choix pour un port prolongé." },
        { title: "Verres", body: "Compatible avec le simple foyer et l'anti-lumière bleue ; pour un progressif, vérifiez la hauteur disponible." },
      ],
      advice:
        "Une monture ovale doit suivre la ligne des sourcils sans la couvrir. C'est le premier repère que vérifie l'opticien à l'essayage.",
      faq: [
        { q: "Les lunettes ovales sont-elles démodées ?", a: "Non : elles reviennent régulièrement, en version métal fin notamment, et restent la forme la plus facile à porter au quotidien." },
        { q: "Quelle taille d'ovale choisir ?", a: "La largeur totale de la monture doit correspondre à celle de votre visage. Nos opticiens prennent la mesure gratuitement en magasin." },
      ],
    },
    en: {
      suitsTitle: "Are oval glasses right for you?",
      suits:
        "The oval is the most neutral shape: neither round nor angular, it fits everyone and softens square or triangular faces without drawing attention to the frame.",
      keyPoints: [
        { title: "Style", body: "Discreet and understated, ideal for a first pair or professional use." },
        { title: "Comfort", body: "Compact lenses and a light frame make it the best choice for long wear." },
        { title: "Lenses", body: "Works with single vision and blue-light lenses; check available height for progressives." },
      ],
      advice:
        "An oval frame should follow the brow line without covering it. That is the first thing the optician checks at fitting.",
      faq: [
        { q: "Are oval glasses out of fashion?", a: "No: they come back regularly, especially in thin metal, and remain the easiest shape to wear daily." },
        { q: "Which oval size should I pick?", a: "Total frame width should match your face width. Our opticians measure it for free in store." },
      ],
    },
  },

  browline: {
    illustration: "browline",
    faceShapes: ["round", "oval"],
    related: [
      { key: "material", valueId: "mixed" },
      { key: "gender", valueId: "men" },
      { key: "color", valueId: "brown" },
    ],
    fr: {
      suitsTitle: "Les lunettes browline sont-elles faites pour vous ?",
      suits:
        "La browline concentre la matière sur la partie haute de la monture, exactement à la place des sourcils. Elle structure le regard et remonte visuellement le visage : très efficace sur les visages ronds ou allongés.",
      keyPoints: [
        { title: "Style", body: "Rétro années 50 revisité, souvent en acétate sur le haut et métal sur le bas." },
        { title: "Confort", body: "Le bas allégé réduit le poids sur le nez malgré une partie supérieure épaisse." },
        { title: "Verres", body: "Bonne hauteur de verre : compatible progressif et anti-lumière bleue." },
      ],
      advice:
        "La barre supérieure doit suivre vos sourcils sans les cacher complètement. Un mauvais alignement est ce qui fait paraître une browline trop grande.",
      faq: [
        { q: "La browline convient-elle à un visage rond ?", a: "Oui : la barre horizontale supérieure allonge le visage et compense la rondeur." },
        { q: "La browline est-elle solide ?", a: "Oui, la partie métal inférieure et le fil de maintien sont conçus pour un usage quotidien. La réparation est gratuite dans nos boutiques." },
      ],
    },
    en: {
      suitsTitle: "Are browline glasses right for you?",
      suits:
        "Browline frames concentrate material along the top, exactly where the eyebrows sit. They structure the gaze and visually lift the face, which works very well on round or long faces.",
      keyPoints: [
        { title: "Style", body: "A revisited 1950s classic, usually acetate on top and metal below." },
        { title: "Comfort", body: "The lighter lower half reduces weight on the nose despite the thick upper bar." },
        { title: "Lenses", body: "Good lens height: compatible with progressive and blue-light lenses." },
      ],
      advice:
        "The top bar should follow your eyebrows without hiding them entirely. Poor alignment is what makes a browline look oversized.",
      faq: [
        { q: "Do browline glasses suit a round face?", a: "Yes: the horizontal top bar lengthens the face and offsets roundness." },
        { q: "Are browline frames durable?", a: "Yes, the lower metal rim and retaining wire are built for daily use. Repairs are free in our stores." },
      ],
    },
  },

  geometric: {
    illustration: "geometric",
    faceShapes: ["oval", "round"],
    related: [
      { key: "material", valueId: "metal" },
      { key: "gender", valueId: "unisex" },
      { key: "color", valueId: "gold" },
    ],
    fr: {
      suitsTitle: "Les lunettes géométriques sont-elles faites pour vous ?",
      suits:
        "Hexagones, octogones, formes anguleuses : la monture géométrique est un choix de style. Elle apporte du relief à un visage ovale ou rond, à condition de rester proportionnée à la largeur du visage.",
      keyPoints: [
        { title: "Style", body: "Contemporain et distinctif, souvent en métal fin pour rester léger visuellement." },
        { title: "Confort", body: "Structure fine, poids réduit ; l'ajustement des plaquettes est déterminant." },
        { title: "Verres", body: "Idéal en simple foyer et en anti-lumière bleue ; vérifiez la hauteur pour un progressif." },
      ],
      advice:
        "Sur une monture géométrique, moins la monture est épaisse, plus la forme reste portable au quotidien. Essayez-la avant d'ajouter une couleur forte.",
      faq: [
        { q: "Les lunettes géométriques se portent-elles tous les jours ?", a: "Oui, en version métal fin et couleur neutre. Les modèles épais et colorés conviennent mieux à un usage occasionnel." },
        { q: "Quelle forme de visage pour une monture géométrique ?", a: "Les visages ovales et ronds la portent le plus facilement. Nos opticiens vous conseillent gratuitement à l'essayage." },
      ],
    },
    en: {
      suitsTitle: "Are geometric glasses right for you?",
      suits:
        "Hexagons, octagons, angular shapes: a geometric frame is a style statement. It adds definition to an oval or round face, as long as it stays proportionate to face width.",
      keyPoints: [
        { title: "Style", body: "Contemporary and distinctive, often in thin metal to stay visually light." },
        { title: "Comfort", body: "Slim structure and low weight; nose-pad adjustment makes all the difference." },
        { title: "Lenses", body: "Ideal for single-vision and blue-light lenses; check height for progressives." },
      ],
      advice:
        "With geometric frames, the thinner the rim, the more wearable the shape day to day. Try it on before adding a strong colour.",
      faq: [
        { q: "Can geometric glasses be worn daily?", a: "Yes, in thin metal and a neutral colour. Thick, colourful models suit occasional wear better." },
        { q: "Which face shape suits geometric frames?", a: "Oval and round faces carry them most easily. Our opticians advise you for free at fitting." },
      ],
    },
  },
};
